import { z } from 'zod';

/** Controlli permanenti e indicatori (Fase 6). */

const ident = z.string().regex(/^[a-z][a-z0-9_]{0,62}$/);
export const CONTROL_FILTER_OPERATORS = ['uguale', 'diverso', 'contiene', 'maggiore', 'minore', 'presente', 'assente', 'uno_tra', 'in_unita'] as const;
/**
 * Filtro su un attributo: maggiore e minore confrontano numeri o date quando il valore lo è; il valore «oggi» (anche
 * «oggi+30», «oggi-90») è la data del calcolo, «@campo» un altro campo della stessa voce. `uno_tra` prende i valori
 * separati da «|». Su un campo sì/no il valore si scrive «sì» o «no». `in_unita` tiene le voci con la quantità in
 * quell'unità (negli indicatori anche in un'unità che vi si converte: i chili accanto ai quintali) (ADR 0213, indicatori mancanti).
 */
const filterSchema = z.object({ attributo: ident, operatore: z.enum(CONTROL_FILTER_OPERATORS), valore: z.string().max(200).default('') });
const filtersSchema = z.array(filterSchema).max(10).default([]);
const keysSchema = z.array(z.object({ a: ident, b: ident, modo: z.enum(['uguale', 'contiene']) })).min(1).max(5);

/**
 * Controlli di gestione (ADR 0018): forme dichiarative che l'archivio esegue in modo deterministico. Sono forme di
 * calcolo, non temi aziendali: cosa controllare lo decide chi progetta il controllo (persona o motore).
 */
const refSchema = z.object({ lato: z.enum(['a', 'b']).default('a'), attributo: ident }).strict();
/** Un termine di una regola: un attributo, un valore fisso, oggi, i giorni tra due date, una data più N giorni. */
export const ControlTermSchema = z.union([
  refSchema,
  z.object({ valore: z.string().max(200) }).strict(),
  z.object({ oggi: z.literal(true) }).strict(),
  z.object({ giorni_tra: z.object({ da: refSchema, a: refSchema }).strict() }).strict(),
  z.object({ data: refSchema, piu_giorni: z.union([z.number().int().min(-3660).max(3660), refSchema]), fine_mese: z.boolean().default(false) }).strict(),
]);
export type ControlTerm = z.infer<typeof ControlTermSchema>;

export const CONTROL_RULE_OPERATORS = ['uguale', 'diverso', 'minore', 'minore_uguale', 'maggiore', 'maggiore_uguale', 'presente'] as const;
/** Regola: sinistra operatore destra, confrontati come numeri, date o testo, con tolleranza (per le date in giorni). */
export const ControlRuleSchema = z.object({
  descrizione: z.string().trim().min(3).max(200),
  sinistra: ControlTermSchema,
  operatore: z.enum(CONTROL_RULE_OPERATORS),
  destra: ControlTermSchema.nullable().default(null),
  confronto: z.enum(['numero', 'data', 'testo']).default('testo'),
  tolleranza: z.number().min(0).max(1e12).default(0),
  tolleranza_percentuale: z.number().min(0).max(100).default(0),
});
export type ControlRule = z.infer<typeof ControlRuleSchema>;

const PERIODS = ['mese', 'trimestre', 'anno'] as const;

export const ControlFormSchema = z.discriminatedUnion('forma', [
  /** Ogni A (filtrato) ha un B con le stesse chiavi; se ci sono regole, almeno un B abbinato le rispetta tutte. */
  z.object({
    forma: z.literal('corrispondenza'),
    tipo_a: ident,
    filtri_a: filtersSchema,
    tipo_b: ident,
    filtri_b: filtersSchema,
    chiavi: keysSchema,
    regole: z.array(ControlRuleSchema).max(5).default([]),
    /** Un A senza nessun B: eccezione o fuori da questo controllo (quando conta solo se i valori tornano). */
    mancante: z.enum(['eccezione', 'ignora']).default('eccezione'),
  }),
  /** Regole su un solo insieme (valori mancanti, fuori intervallo, scadenze rispetto a oggi) e doppioni per chiave. */
  z.object({
    forma: z.literal('regola'),
    tipo_a: ident,
    filtri_a: filtersSchema,
    regole: z.array(ControlRuleSchema).max(5).default([]),
    doppioni: z.array(ident).max(3).default([]),
  }),
  /** Per ogni A attivo in un periodo chiuso c'è almeno un B con data in quel periodo. */
  z.object({
    forma: z.literal('periodicita'),
    tipo_a: ident,
    filtri_a: filtersSchema,
    tipo_b: ident,
    filtri_b: filtersSchema,
    chiavi: keysSchema,
    data_b: ident,
    inizio_a: ident.nullable().default(null),
    fine_a: ident.nullable().default(null),
    periodo: z.enum(PERIODS),
    periodi: z.number().int().min(1).max(24).default(3),
  }),
  /** La somma (o il conteggio) dei B di ogni A rispetta un limite. */
  z.object({
    forma: z.literal('totale'),
    tipo_a: ident,
    filtri_a: filtersSchema,
    tipo_b: ident,
    filtri_b: filtersSchema,
    chiavi: keysSchema,
    metrica: z.enum(['somma', 'conteggio']),
    attributo_b: ident.nullable().default(null),
    operatore: z.enum(['minore_uguale', 'maggiore_uguale', 'uguale']),
    limite: ControlTermSchema,
    tolleranza: z.number().min(0).max(1e12).default(0),
    tolleranza_percentuale: z.number().min(0).max(100).default(0),
  }),
  /** L'ultimo periodo chiuso di un indicatore si scosta dalla media dei precedenti. */
  z.object({
    forma: z.literal('andamento'),
    tipo_a: ident,
    filtri_a: filtersSchema,
    metrica: z.enum(['conteggio', 'somma', 'media']),
    attributo: ident.nullable().default(null),
    attributo_data: ident,
    grana: z.enum(PERIODS).default('mese'),
    periodi: z.number().int().min(2).max(24).default(6),
    scarto_percentuale: z.number().min(5).max(1000),
    direzione: z.enum(['entrambe', 'aumento', 'calo']).default('entrambe'),
  }),
]);
export type ControlForm = z.infer<typeof ControlFormSchema>;
export type ControlFormInput = z.input<typeof ControlFormSchema>;

/** Attributo che serve a un controllo e sta nei documenti: si aggiunge al tipo del modello e si ricava con citazione. */
export const RequiredAttributeSchema = z.object({
  tipo: ident,
  nome: ident,
  tipo_valore: z.enum(['testo', 'numero', 'data']),
  etichetta: z.string().trim().min(1).max(120),
  descrizione: z.string().trim().max(500).default(''),
});
export type RequiredAttribute = z.infer<typeof RequiredAttributeSchema>;

export const CreateControlSchema = z.object({
  name: z.string().trim().min(3).max(120),
  description: z.string().trim().max(500).optional(),
  /** Cosa verificare (ADR 0018). Senza, vale la forma storica qui sotto: ogni A ha un corrispondente in B. */
  verifica: ControlFormSchema.optional(),
  tipo_a: ident.optional(),
  filtri_a: filtersSchema,
  tipo_b: ident.optional(),
  chiavi: keysSchema.optional(),
  /** Attributi di A da mostrare nel compito di verifica. */
  campi: z.array(ident).min(1).max(10),
  /** Ruolo che verifica le eccezioni ed esiti possibili; l'ultimo esito vale "falso allarme". */
  ruolo: ident,
  esiti: z.array(ident).min(2).max(5).default(['da_sistemare', 'non_e_un_problema']),
  /** Dati che il controllo legge dai documenti (attributi aggiunti ai tipi del modello). */
  dati_richiesti: z.array(RequiredAttributeSchema).max(10).default([]),
});
export type CreateControlInput = z.infer<typeof CreateControlSchema>;

/** Esito di un'esecuzione o di una prova: eccezioni con il motivo e copertura dei dati. */
export interface ControlCheckResult {
  totale: number;
  verificabili: number;
  non_verificabili: number;
  eccezioni: Array<{ rif: string; attributi: Record<string, unknown>; motivo: string }>;
  esempi_non_verificabili: Array<{ rif: string; motivo: string }>;
}

export interface ControlDto {
  id: string;
  name: string;
  description: string | null;
  config: CreateControlInput;
  /** Forma effettiva e sua lettura in italiano. */
  verifica: ControlForm;
  spiegazione: string;
  process_id: string;
  enabled: boolean;
  last_run_at: string | null;
  last_result: { totale: number; abbinate: number; nuove: number; aperte: number; risolte: number; non_verificabili?: number } | null;
  /** Esiti delle verifiche: quante eccezioni erano vere e quante falsi allarmi. */
  outcomes: Record<string, number>;
  open_exceptions: number;
}

export const PreviewControlSchema = z.object({ verifica: ControlFormSchema });

/**
 * Come si misura (ADR 0213): conteggio delle voci, somma o media di un campo numerico, `giorni` = la somma dei giorni
 * dalla data del periodo al campo data `attributo` (le notti di un soggiorno, dall'arrivo alla partenza; con
 * `attributo` = «oggi» fino al giorno del calcolo), o `distinti` = quanti valori diversi ha il campo.
 */
export const KPI_METRICS = ['conteggio', 'somma', 'media', 'giorni', 'distinti'] as const;

/**
 * Come dividere il valore (ADR 0213 § 3), a un passo solo: per un campo della voce, per sede o per attività (i legami
 * dell'ADR 0057), o per un campo della voce collegata (`campo` è il collegamento, il cliente della fattura; `tipo` il
 * tipo collegato; `campo_collegato` il campo da leggere lì, la provincia del cliente). Nei tipi della forma nuova la voce
 * collegata si trova anche dai legami del tipo verso `tipo` (ADR 0238, `_voci_legate`): lì `campo` può non esistere.
 */
export const KpiGroupSchema = z.discriminatedUnion('per', [
  z.object({ per: z.literal('campo'), campo: ident }),
  z.object({ per: z.literal('sede') }),
  z.object({ per: z.literal('attivita') }),
  z.object({ per: z.literal('collegato'), campo: ident, tipo: ident, campo_collegato: ident }),
]);
export type KpiGroup = z.infer<typeof KpiGroupSchema>;

/**
 * Ciò che una misura può chiedere in più (ADR 0213, indicatori mancanti): la data del periodo letta dalla voce collegata
 * (la riga prende la data della sua consegna), la quantità in un'unità sola (le altre dello stesso genere si convertono,
 * quelle di un altro genere restano fuori e si contano nella copertura) e il prodotto di due campi (chili × prezzo al
 * chilo).
 */
const measureExtras = {
  /** Il tipo collegato da cui viene `attributo_data`: la voce legata a quella del tipo (il genitore, la testata). */
  periodo_da: ident.nullish(),
  /** Somma o media della quantità in quest'unità (kg, t, m3…). */
  in_unita: z.string().trim().min(1).max(20).nullish(),
  moltiplica_per: ident.nullish(),
};

/** Una misura: conteggio, somma o media di un campo delle voci di un tipo, con i filtri e la data del periodo. */
export const KpiMeasureSchema = z.object({
  tipo: ident,
  metrica: z.enum(KPI_METRICS),
  attributo: ident.nullable().default(null),
  filtri: filtersSchema,
  /** Senza data la misura vale per tutto il tempo: in un rapporto divide ogni periodo (le camere dell'albergo). */
  attributo_data: ident.nullable().default(null),
  /** Nel divisore: moltiplicato per i giorni del periodo (le camere disponibili nel mese = camere × giorni del mese). */
  giorni_del_periodo: z.boolean().default(false),
  ...measureExtras,
  /**
   * Nel divisore di un rapporto diviso per un campo o per una voce collegata: come dividere il divisore, quando è di un
   * altro tipo (i gruppi si incontrano per valore). Dello stesso tipo vale il raggruppamento dell'indicatore.
   */
  raggruppa: KpiGroupSchema.nullish(),
});
export type KpiMeasure = z.infer<typeof KpiMeasureSchema>;


export const KPI_GRAINS = ['giorno', 'mese', 'anno'] as const;

export const CreateKpiSchema = z.object({
  name: z.string().trim().min(2).max(120),
  // La misura (il numeratore in un rapporto).
  tipo: ident,
  metrica: z.enum(KPI_METRICS),
  attributo: ident.nullable().default(null),
  filtri: filtersSchema,
  attributo_data: ident.nullable().default(null),
  ...measureExtras,
  grana: z.enum(KPI_GRAINS).nullable().default(null),
  /** Rapporto (ADR 0213 § 3): il valore è la misura divisa per questa, periodo per periodo. */
  diviso_per: KpiMeasureSchema.nullable().default(null),
  raggruppa: KpiGroupSchema.nullable().default(null),
  /** Solo i dati di questa sede; null = tutta l'azienda. */
  sede: z.string().uuid().nullable().default(null),
  /** Con la grana: accanto a ogni periodo il valore dello stesso periodo dell'anno prima. */
  anno_prima: z.boolean().default(false),
  /** Come si mostra, per un indicatore creato da una persona (quelli della libreria hanno la loro scheda). */
  grafico: z.enum(['linea', 'colonne', 'barre', 'numero']).nullable().default(null),
  unita: z.enum(['euro', 'percentuale', 'numero', 'giorni']).nullable().default(null),
});
export type CreateKpiInput = z.infer<typeof CreateKpiSchema>;

/**
 * Chi vede un indicatore nella dashboard (ADR 0213 § 5): solo chi l'ha creato, tutti, o le persone dei ruoli indicati.
 * Tutti e i ruoli li sceglie il titolare o un amministratore; senza scelta un responsabile crea per tutti, gli altri per sé.
 */
export const KPI_VISIBILITY = ['mia', 'tutti', 'ruoli'] as const;
export const CreateKpiRequestSchema = CreateKpiSchema.extend({
  visibilita: z.enum(KPI_VISIBILITY).nullable().default(null),
  ruoli: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
});
export type CreateKpiRequest = z.infer<typeof CreateKpiRequestSchema>;

/** La dashboard di una persona a un livello: l'ordine dei grafici e quelli tolti (da rimettere). */
export const SaveDashboardSchema = z.object({
  livello: z.union([z.literal('azienda'), z.string().uuid()]),
  ordine: z.array(z.string().uuid()).max(200),
  nascosti: z.array(z.string().uuid()).max(200),
});
export type SaveDashboardInput = z.infer<typeof SaveDashboardSchema>;

/** Un punto della serie. `gruppo` c'è solo con il raggruppamento, `anno_prima` solo se chiesto, le parti solo nel rapporto. */
export interface KpiPoint {
  periodo: string;
  /** Null quando non si può calcolare (rapporto con divisore zero o assente): mai uno zero inventato. */
  valore: number | null;
  righe: number;
  /** Il gruppo (valore del campo, id della sede o dell'attività; null = senza) e il suo nome. */
  gruppo?: string | null;
  etichetta?: string;
  anno_prima?: number | null;
  numeratore?: number;
  denominatore?: number | null;
}

export interface KpiCoverage {
  righe_totali: number;
  righe_escluse: number;
  fonti: Array<{ fonte: string; righe: number; ultimo_aggiornamento: string }>;
}

/**
 * La scheda di un indicatore dell'azienda tipo (ADR 0213): da dove viene nella libreria, a cosa serve, come si mostra e
 * quali dati richiede (tipi dell'archivio dell'azienda).
 */
export interface KpiLibraryMeta {
  chiave: string;
  strato: string;
  a_cosa_serve: string;
  unita: 'euro' | 'percentuale' | 'numero' | 'giorni';
  dove: 'azienda' | 'sede';
  grafico: 'linea' | 'colonne' | 'barre' | 'numero';
  verso_buono: 'alto' | 'basso' | null;
  fonte: { base: 'norma' | 'standard' | 'prassi'; testo: string };
  nota: string | null;
  richiede: Array<{ tipo: string; nome: string; campi: string[] }>;
}

/** Un dato che manca per vedere l'indicatore: il tipo senza voci, o con le voci ma senza i campi che servono. */
export interface KpiMissing {
  tipo: string;
  nome: string;
  motivo: 'nessuna_voce' | 'campi_vuoti';
  campi: string[];
}

export interface KpiDto {
  id: string;
  name: string;
  /** Solo per gli indicatori dell'azienda tipo. */
  libreria?: KpiLibraryMeta;
  /** Cosa serve per vederlo: l'indicatore non mostra numeri finché manca (ADR 0213 § 2). */
  manca?: KpiMissing[];
  /** Chi lo vede e se l'ha creato chi chiede (per togliere o eliminare). */
  visibilita?: (typeof KPI_VISIBILITY)[number];
  ruoli?: string[];
  mio?: boolean;
  config: CreateKpiInput;
  version: number;
  serie: KpiPoint[];
  copertura: KpiCoverage;
  /** Nel rapporto: la copertura del divisore. */
  copertura_divisore?: KpiCoverage;
  spiegazione: string;
  errore: string | null;
  /**
   * Perché chi chiede non lo può vedere: i suoi ruoli non leggono un tipo o un campo della formula (ADR 0213 § 5, ADR
   * 0232). Un indicatore chiuso non ha numeri; per il titolare e gli amministratori non è mai chiuso.
   */
  chiuso?: string;
}

/** La dashboard di chi chiede a un livello: i grafici nell'ordine scelto e quelli tolti, da rimettere. */
export interface DashboardDto {
  visibili: KpiDto[];
  nascosti: Array<{ id: string; name: string }>;
}
